import React from 'react';
import { Check, X } from 'lucide-react';
import { AnswerShapeIcon } from './AnswerShapeIcon';

interface Option {
  id: string;
  text: string;
}

interface Props {
  options: Option[];
  selectedOptionId?: string;
  onSelect: (optionId: string) => void;
  disabled?: boolean;
  correctOptionIds?: string[];
  distribution?: Record<string, number>;
  totalAnswers?: number;
}

/**
 * Verdadero / falso.
 *
 * Usa dos de las cuatro siluetas —hexágono y luna— en lugar de un par
 * verde/rojo. Teñir "verdadero" de verde y "falso" de rojo sugiere que una
 * opción es la buena antes de contestar, y además es justo la señal que peor
 * lee quien confunde ambos tonos.
 */
export const TrueFalseQuestion: React.FC<Props> = ({
  options,
  selectedOptionId,
  onSelect,
  disabled = false,
  correctOptionIds,
  distribution,
  totalAnswers = 0,
}) => {
  const revealed = Array.isArray(correctOptionIds);

  const styles = [
    { button: 'bg-sky-600 hover:bg-sky-500 border-sky-400', ring: 'ring-sky-300', shape: 'hex' as const },
    { button: 'bg-magenta-600 hover:bg-magenta-500 border-magenta-400', ring: 'ring-magenta-300', shape: 'moon' as const },
  ];

  // Dos columnas también en el teléfono: son dos botones y así caen los dos
  // bajo el pulgar sin tener que bajar.
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 w-full max-w-3xl mx-auto">
      {options.map((opt, index) => {
        const style = styles[index % styles.length];
        const isSelected = selectedOptionId === opt.id;
        const isCorrect = revealed && correctOptionIds!.includes(opt.id);
        const isWrongPick = revealed && isSelected && !isCorrect;

        const dimmed = revealed && !isCorrect;
        let tone = style.button;
        if (revealed) {
          tone = isCorrect
            ? 'bg-success-500 border-success-400'
            : isWrongPick
              ? 'bg-danger-500/10 border-danger-500'
              : 'bg-ink-850 border-ink-700 opacity-70';
        }

        const share =
          distribution && totalAnswers > 0
            ? Math.round(((distribution[opt.id] ?? 0) / totalAnswers) * 100)
            : null;

        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => !disabled && onSelect(opt.id)}
            disabled={disabled}
            aria-pressed={isSelected}
            className={`
              relative overflow-hidden flex flex-col items-center justify-center gap-3
              px-3 sm:px-6 py-8 sm:py-10 rounded-2xl border-2 transition-all duration-200
              ${tone}
              ${isSelected && !revealed ? `ring-4 ring-offset-2 ring-offset-ink-950 ${style.ring} scale-[1.02]` : ''}
              ${disabled ? 'cursor-default' : 'cursor-pointer hover:scale-[1.02] active:scale-[0.99]'}
            `}
          >
            {share !== null && (
              <span
                className="absolute inset-x-0 bottom-0 bg-white/12 transition-[height] duration-700"
                style={{ height: `${share}%` }}
                aria-hidden="true"
              />
            )}

            <span className={`relative ${dimmed ? 'text-ink-500' : 'text-white/95'}`}>
              <AnswerShapeIcon shape={style.shape} className="w-10 h-10" />
            </span>
            <span className={`relative font-bold text-xl sm:text-2xl ${dimmed ? 'text-ink-200' : 'text-white'}`}>
              {opt.text}
            </span>

            {share !== null && (
              <span className={`relative text-sm font-mono tabular-nums ${dimmed ? 'text-ink-400' : 'text-white/80'}`}>
                {share}% de la sala
              </span>
            )}

            {revealed && (
              <span className="relative">
                {isCorrect ? (
                  <Check className="w-7 h-7 text-white" aria-label="Respuesta correcta" />
                ) : isWrongPick ? (
                  <X className="w-7 h-7 text-danger-400" aria-label="Tu respuesta, incorrecta" />
                ) : null}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
